/**
 * Reine Datumslogik der Gewerke-Ueberschneidung.
 *
 * Hier steht alles, was ohne DOM, ohne Supabase und ohne jsdom entscheidbar
 * ist — das Projekt uebersetzt Tests in der Node-Umgebung (kein jsdom), also
 * ist eine Regel nur dann geprueft, wenn sie hier liegt und nicht in einer
 * Komponente. Stehende Entscheidung im Repo: duenner Container, Logik in
 * Hooks und puren Funktionen.
 *
 * ─── Warum hier KEIN `Date` vorkommt ─────────────────────────────────────
 * Die Vorlage aus der Quell-App (`sige-plan/src/lib/hazard-matrix.ts`) baute
 * aus jedem ISO-Datum ein `Date`, verglich Millisekunden und schrieb das
 * Ergebnis mit `toISOString().split('T')[0]` zurueck (dort Zeile 26-32).
 * Damit haengt das Ergebnis an der Zeitzone des Rechners, auf dem gerechnet
 * wird: dieselbe Baustelle ergaebe in einer anderen Zeitzone einen anderen
 * Ueberschneidungsbeginn, und dieser Wert wandert in den SiGe-Plan.
 * Ein ISO-Tagesdatum (JJJJ-MM-TT) ist fest sortiert — Vergleich und Klemmung
 * gehen lexikalisch, genau wie in `src/components/sige-plan/zeitraum.ts:6-11`.
 * Die Tagesdifferenz rechnen wir aus den Zahlen selbst (Kalenderformel weiter
 * unten). Diese Datei benutzt daher weder `new Date()` noch `Date.UTC()` noch
 * `toISOString()` — der Test sperrt `Date` waehrend des Laufs, damit das so
 * bleibt.
 *
 * ─── Warum ein gemeinsamer Tag zaehlt ────────────────────────────────────
 * Die Vorlage verwarf ihn (`if (overlapStart >= overlapEnd) return null`,
 * dort Zeile 29). Auf einer Baustelle ist ein gemeinsamer Kalendertag eine
 * echte Ueberschneidung: der Kranhub ueber dem besetzten Geruest dauert
 * Sekunden. Deshalb `>` statt `>=`, und `dauerTage` zaehlt beide Grenzen mit
 * (gleicher Tag = 1, nicht 0).
 */

import type {
    Ampel,
    GewerkOhneZeitraum,
    GewerkZeitraum,
    Ueberschneidung,
    UeberschneidungsPaar,
    Zeitraum,
    ZeitraumMangel,
} from './types';

// ═══════════════════════════════════════════════════════════════════════════
// ISO-TAGESDATUM — Pruefung und Kalenderarithmetik ohne `Date`
// ═══════════════════════════════════════════════════════════════════════════

const ISO_TAG = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Nur echte ISO-Tagesdaten durchlassen — sonst `null`.
 *
 * Die Spalten `zeitraum_von`/`_bis` sind zwar vom Typ `date`, aber die Werte
 * laufen durch KI-Vorschlaege und Freitext-Import; die KI liefert auch
 * "KW 12" oder "Maerz 2026" (siehe `zeitraum.ts:22-27`). Ein solcher Wert
 * darf nicht lexikalisch mitverglichen werden — "KW 12" ist groesser als
 * jedes Datum und haette jede Ueberschneidung stillschweigend verschluckt.
 */
export const alsTag = (wert: string | null | undefined): string | null => {
    if (typeof wert !== 'string') return null;
    const roh = wert.trim();
    if (!ISO_TAG.test(roh)) return null;
    const monat = Number(roh.slice(5, 7));
    const tag = Number(roh.slice(8, 10));
    // Grobe Plausibilitaet. Die genaue Monatslaenge prueft die Datenbank
    // (Spaltentyp `date`); hier geht es nur darum, offensichtlichen Unsinn
    // wie "2026-13-45" nicht als Datum weiterzureichen.
    if (monat < 1 || monat > 12 || tag < 1 || tag > 31) return null;
    return roh;
};

/**
 * Fortlaufende Tagesnummer eines ISO-Datums (Tage seit 1970-01-01).
 *
 * Kalenderformel nach dem "days from civil"-Verfahren: sie kennt die
 * Schaltjahrregel einschliesslich der 100- und 400-Jahre-Ausnahme und
 * braucht dafuer kein `Date`-Objekt — und damit auch keine Zeitzone.
 * Der Rueckgabewert ist nur als Differenz gedacht, nie als Zeitstempel.
 */
const tagesNummer = (iso: string): number => {
    const jahrRoh = Number(iso.slice(0, 4));
    const monat = Number(iso.slice(5, 7));
    const tag = Number(iso.slice(8, 10));

    // Das Rechenjahr beginnt im Maerz, damit der Schalttag ans Jahresende
    // faellt und keine Fallunterscheidung mehr braucht.
    const jahr = jahrRoh - (monat <= 2 ? 1 : 0);
    const aera = Math.floor(jahr / 400);
    const jahrInAera = jahr - aera * 400;                        // 0..399
    const tagImJahr = Math.floor((153 * (monat + (monat > 2 ? -3 : 9)) + 2) / 5) + tag - 1;
    const tagInAera = jahrInAera * 365
        + Math.floor(jahrInAera / 4)
        - Math.floor(jahrInAera / 100)
        + tagImJahr;
    return aera * 146097 + tagInAera - 719468;
};

/** Kalendertage zwischen zwei ISO-Tagen, BEIDE Grenzen eingeschlossen.
 *  Gleicher Tag ergibt 1 — ein Bautag ist ein Bautag. */
export const tageEinschliesslich = (von: string, bis: string): number =>
    tagesNummer(bis) - tagesNummer(von) + 1;

// ═══════════════════════════════════════════════════════════════════════════
// AMPEL
// ═══════════════════════════════════════════════════════════════════════════

/** Ab wie vielen Tagen die Zelle rot wird. Aus der Vorlage uebernommen
 *  (`hazard-matrix.ts:77-80`): mehr als vier Wochen paralleler Betrieb ist
 *  keine Beruehrung mehr, sondern Dauerzustand — er braucht eine
 *  organisatorische Regelung, nicht nur eine Absprache. */
export const AMPEL_SCHWELLE_ROT = 28;

/**
 * Farbe der Matrixzelle aus der Ueberschneidungsdauer.
 *
 * Grau heisst ausdruecklich "keine gemeinsame Zeit", nicht "unbekannt":
 * Gewerke ohne verwertbaren Zeitraum stehen gar nicht in der Matrix, sondern
 * in der Liste aus `gewerkeOhneZeitraum`.
 */
export const ampel = (dauerTage: number): Ampel => {
    if (typeof dauerTage !== 'number' || !isFinite(dauerTage)) return 'grau';
    if (dauerTage > AMPEL_SCHWELLE_ROT) return 'rot';
    if (dauerTage >= 1) return 'orange';
    return 'grau';
};

// ═══════════════════════════════════════════════════════════════════════════
// UEBERSCHNEIDUNG ZWEIER ZEITRAEUME
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Gemeinsames Zeitfenster zweier Zeitraeume — oder `null`.
 *
 * `null` bedeutet zweierlei und wird hier bewusst nicht unterschieden:
 * entweder fehlt ein Datum, oder es gibt keine gemeinsame Zeit. Fuer die
 * Matrix ist beides "keine Zelle"; der Unterschied wird an anderer Stelle
 * sichtbar gemacht (`gewerkeOhneZeitraum`), damit ein fehlendes Datum nicht
 * als geprueftes "keine Ueberschneidung" durchgeht.
 *
 * Beruehrung ohne Ueberlappung — a endet am 10., b beginnt am 11. — ergibt
 * `von > bis` und damit `null`. Ein gemeinsamer Tag ergibt `von === bis`
 * und damit `dauerTage === 1`.
 */
export const berechneUeberschneidung = (a: Zeitraum, b: Zeitraum): Ueberschneidung | null => {
    const aVon = alsTag(a && a.von);
    const aBis = alsTag(a && a.bis);
    const bVon = alsTag(b && b.von);
    const bBis = alsTag(b && b.bis);
    if (!aVon || !aBis || !bVon || !bBis) return null;

    // Verdrehte Zeitraeume werden nicht "geheilt". Ein getauschter Anfang
    // waere eine stille Korrektur an Plandaten, die dem Anwender gehoeren;
    // er sieht den Zahlendreher stattdessen in der Ausschussliste.
    if (aVon > aBis || bVon > bBis) return null;

    const von = aVon > bVon ? aVon : bVon;   // der spaetere Beginn
    const bis = aBis < bBis ? aBis : bBis;   // das fruehere Ende
    if (von > bis) return null;              // `>` — der eine gemeinsame Tag zaehlt

    return { von, bis, dauerTage: tageEinschliesslich(von, bis) };
};

// ═══════════════════════════════════════════════════════════════════════════
// ALLE PAARE
// ═══════════════════════════════════════════════════════════════════════════

/** Lexikalischer Vergleich ohne `localeCompare`: dessen Ergebnis haengt an
 *  der ICU-Zusammenstellung der Laufzeit, und eine Reihenfolge, die sich
 *  zwischen Browser und Node unterscheidet, ist keine Reihenfolge. */
const vergleiche = (x: string, y: string): number => (x < y ? -1 : x > y ? 1 : 0);

/**
 * Alle Gewerkepaare mit gemeinsamer Zeit, laengste Ueberschneidung zuerst.
 *
 * Reihenfolge der Kriterien: `dauerTage` absteigend, dann die Gewerkenamen,
 * zuletzt die Eingangsreihenfolge. Der letzte Schluessel ist kein Zierrat —
 * er macht das Ergebnis unabhaengig davon, ob die Sortierung der Laufzeit
 * stabil ist, und darauf beruht die Zusicherung, dass zwei Laeufe ueber
 * dieselben Daten dieselbe Liste ergeben. Die Reihenfolge landet im PDF und
 * im Besprechungsprotokoll; sie darf sich nicht ohne Datenaenderung bewegen.
 *
 * Zwei Eintraege mit demselben `norm` werden uebersprungen: ein Gewerk
 * gefaehrdet sich nicht selbst, und die Datenbank laesst ein solches Paar
 * ohnehin nicht zu (CHECK `gewerk_a_norm < gewerk_b_norm`). Die Eingabe ist
 * bereits je `norm` gruppiert.
 */
export const berechneAlleUeberschneidungen = (gewerke: GewerkZeitraum[]): UeberschneidungsPaar[] => {
    const liste = Array.isArray(gewerke) ? gewerke : [];
    const gefunden: { paar: UeberschneidungsPaar; rang: number }[] = [];

    for (let i = 0; i < liste.length; i++) {
        for (let j = i + 1; j < liste.length; j++) {
            const a = liste[i];
            const b = liste[j];
            if (!a || !b || a.norm === b.norm) continue;

            const fenster = berechneUeberschneidung(a, b);
            if (!fenster) continue;

            // Feste Paarreihenfolge, sonst entstuenden je Paar zwei
            // spiegelbildliche Datensaetze mit getrennten Bewertungen.
            const erst = a.norm < b.norm ? a : b;
            const zweit = a.norm < b.norm ? b : a;

            gefunden.push({
                rang: gefunden.length,
                paar: {
                    aNorm: erst.norm,
                    aLabel: erst.label,
                    bNorm: zweit.norm,
                    bLabel: zweit.label,
                    von: fenster.von,
                    bis: fenster.bis,
                    dauerTage: fenster.dauerTage,
                    ampel: ampel(fenster.dauerTage),
                },
            });
        }
    }

    gefunden.sort((x, y) =>
        y.paar.dauerTage - x.paar.dauerTage
        || vergleiche(x.paar.aNorm, y.paar.aNorm)
        || vergleiche(x.paar.bNorm, y.paar.bNorm)
        || vergleiche(x.paar.aLabel, y.paar.aLabel)
        || vergleiche(x.paar.bLabel, y.paar.bLabel)
        || x.rang - y.rang
    );

    return gefunden.map(eintrag => eintrag.paar);
};

// ═══════════════════════════════════════════════════════════════════════════
// AUSSCHUSS — sichtbar, nicht stillschweigend
// ═══════════════════════════════════════════════════════════════════════════

const HINWEIS: Record<ZeitraumMangel, string> = {
    kein_datum: 'Kein Zeitraum im SiGe-Plan hinterlegt — das Gewerk kann nicht auf Ueberschneidungen geprueft werden.',
    kein_beginn: 'Beginn fehlt — nur das Ende ist hinterlegt.',
    kein_ende: 'Ende fehlt — nur der Beginn ist hinterlegt.',
    ungueltiges_format: 'Der Zeitraum ist kein Tagesdatum (erwartet JJJJ-MM-TT).',
    ende_vor_beginn: 'Das Ende liegt vor dem Beginn — Zahlendreher im Terminplan.',
};

/** Welcher Mangel haelt diesen Zeitraum aus der Analyse heraus? `null` heisst
 *  "verwertbar". */
export const zeitraumMangel = (z: Zeitraum): ZeitraumMangel | null => {
    const vonRoh = z && typeof z.von === 'string' ? z.von.trim() : '';
    const bisRoh = z && typeof z.bis === 'string' ? z.bis.trim() : '';
    if (!vonRoh && !bisRoh) return 'kein_datum';
    if (!vonRoh) return 'kein_beginn';
    if (!bisRoh) return 'kein_ende';

    const von = alsTag(vonRoh);
    const bis = alsTag(bisRoh);
    if (!von || !bis) return 'ungueltiges_format';
    if (von > bis) return 'ende_vor_beginn';
    return null;
};

/**
 * Die Gewerke, die aus der Analyse herausfallen — in Eingangsreihenfolge.
 *
 * Diese Liste gehoert in jede Ansicht und in jedes PDF neben die Matrix. Ein
 * Gewerk ohne Datum verschwinden zu lassen erzeugt eine vollstaendig
 * aussehende Matrix, in der ausgerechnet das ungeplante Gewerk fehlt — und
 * ein SiGe-Plan, der etwas nicht zeigt, wird als "geprueft und ohne Befund"
 * gelesen.
 */
export const gewerkeOhneZeitraum = (gewerke: GewerkZeitraum[]): GewerkOhneZeitraum[] => {
    const liste = Array.isArray(gewerke) ? gewerke : [];
    const ausschuss: GewerkOhneZeitraum[] = [];
    for (const gewerk of liste) {
        if (!gewerk) continue;
        const grund = zeitraumMangel(gewerk);
        if (!grund) continue;
        ausschuss.push({ gewerk, grund, hinweis: HINWEIS[grund] });
    }
    return ausschuss;
};
